import { baseUrl, signOut, token } from "./api.ts";

/**
 * The store's realtime stream, as the till hears it
 * (`04-api-surface.md` § Realtime).
 *
 * The till polled for occupancy, and the reason it gave was true at the time:
 * the API's event bus was in-process, so a second instance would leave a
 * terminal quietly out of date — which on a table picker is worse than a poll,
 * because it looks correct. The bus is fanned out through Redis now, so
 * subscribing is honest, and a table that frees up appears when it frees up
 * rather than up to ten seconds later.
 *
 * Not `EventSource`. That API cannot set an `Authorization` header, and the
 * till authenticates with a bearer token — its own comment in `api.ts` explains
 * why it cannot use a cookie. So this reads the stream off `fetch`, which is
 * about thirty lines and no dependency.
 *
 * Nothing here holds application state. An event says *what changed*, never
 * what it changed to, and the caller re-fetches — which is the shape
 * § Realtime specifies and the reason a stale event cannot show a wrong table.
 */

export type StoreEventType =
  "orders.new" | "orders.updated" | "table.changed" | "menu.availability";

export type StoreEvent = {
  readonly type: StoreEventType;
  readonly entityId?: string;
  readonly at?: string;
  /**
   * A message key the terminal should read aloud
   * (`STORE_SETTINGS_VOICE_BROADCAST`), or absent.
   *
   * A key rather than a sentence, for the same reason a report column is one:
   * the till speaks whatever language it is set to, and a server that sent
   * English words would have a Khmer restaurant's terminal announcing orders
   * in English. Present only where the merchant bought the feature and
   * switched it on — the server decides that, not the terminal.
   */
  readonly speak?: string;
};

/**
 * Reconnect backoff.
 *
 * A service losing its wifi for a minute must not become a terminal hammering
 * the API sixty times, and a terminal that gave up after three tries is one a
 * cashier has to know to restart. So: quick at first, then patient, and never
 * stopping.
 */
const BACKOFF_MS = [1000, 2000, 5000, 10_000, 20_000];

export type StreamHandle = { close(): void };

export function subscribeToStoreEvents(
  onEvent: (event: StoreEvent) => void,
  onStatus?: (connected: boolean) => void,
): StreamHandle {
  let closed = false;
  let controller: AbortController | null = null;
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  async function run(): Promise<void> {
    while (!closed) {
      // No credential means the sign-in screen is up. Waiting is right: the
      // stream resumes when there is a token, and a doomed request every
      // second in the meantime is the same bug `Till.drain` guards against.
      if (token() === null) {
        await sleep(2000);
        continue;
      }

      controller = new AbortController();

      try {
        const response = await fetch(`${baseUrl()}/api/store/events`, {
          headers: { Authorization: `Bearer ${token()}`, Accept: "text/event-stream" },
          signal: controller.signal,
        });

        if (response.status === 401) {
          // Same rule as `api()`: no retry fixes a dead credential.
          signOut();
          await sleep(2000);
          continue;
        }
        if (!response.ok || !response.body) throw new Error(`stream: ${response.status}`);

        attempt = 0;
        onStatus?.(true);
        await read(response.body, onEvent, () => closed);
      } catch {
        // Every failure lands here — a dropped connection, a refused one, an
        // abort on close — and all of them mean the same thing to a till.
      }

      onStatus?.(false);
      if (closed) return;
      await sleep(BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)]!);
      attempt += 1;
    }
  }

  function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      timer = setTimeout(resolve, ms);
    });
  }

  void run();

  return {
    close() {
      closed = true;
      if (timer) clearTimeout(timer);
      controller?.abort();
    },
  };
}

/**
 * Reads `text/event-stream` off a body.
 *
 * SSE frames are separated by a blank line and a frame can arrive split across
 * chunks, so the buffer is only consumed up to the last complete separator.
 * Parsing per chunk instead is the classic bug here: it works on a fast local
 * connection and drops half the events on a slow one, which is the connection
 * a restaurant actually has.
 */
async function read(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: StoreEvent) => void,
  isClosed: () => boolean,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (!isClosed()) {
    const { done, value } = await reader.read();
    if (done) return;

    buffer += decoder.decode(value, { stream: true });

    let separator = buffer.indexOf("\n\n");
    while (separator !== -1) {
      const frame = buffer.slice(0, separator);
      buffer = buffer.slice(separator + 2);
      const parsed = parseFrame(frame);
      if (parsed) onEvent(parsed);
      separator = buffer.indexOf("\n\n");
    }
  }
}

/** One frame. Returns null for the heartbeat and the ready line. */
export function parseFrame(frame: string): StoreEvent | null {
  let type: string | null = null;
  const data: string[] = [];

  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) type = line.slice(6).trim();
    // A `data:` line may repeat; the spec joins them with a newline.
    else if (line.startsWith("data:")) data.push(line.slice(5).trim());
  }

  // `ping` keeps a proxy from closing an idle connection and `ready` confirms
  // the subscription. Neither is a change, and treating them as one would have
  // the till refetch its tables every twenty seconds forever.
  if (!type || type === "ping" || type === "ready") return null;
  if (
    type !== "orders.new" &&
    type !== "orders.updated" &&
    type !== "table.changed" &&
    type !== "menu.availability"
  ) {
    return null;
  }

  try {
    const payload = JSON.parse(data.join("\n")) as {
      entityId?: string;
      at?: string;
      speak?: string;
    };
    return { type, ...payload };
  } catch {
    return { type };
  }
}
